import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  loadStripe,
  type BillingDetails,
  type ConfirmPaymentData,
  type ConfirmSetupData,
} from '@stripe/stripe-js';
import { CreditCard, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CountrySelect } from '@/components/ui/country-select';
import type {
  PublicCheckoutFieldSetting,
  PublicCheckoutFieldKey,
  PublicCheckoutCountryOptions,
} from '@/types/public-checkout';
import type {
  CustomerCheckoutStripeConfig,
  CustomerSubscriptionCheckoutContext,
  CustomerSubscriptionCheckoutIntent,
} from '@/types/customer-subscription-checkout';
import type { CustomerSubscriptionCheckoutConfirmRequest } from '@/types/customer-subscription-checkout';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { buildStripeAppearance } from '@/lib/stripe/appearance';
import { isStripeReady, resolveStripeAccountOption } from '@/lib/stripe/isStripeReady';
import { resolveStripeLocale } from '@/lib/stripe/locale';
import { logger } from '@/lib/logger';

interface StripePaymentFormProps {
  context: CustomerSubscriptionCheckoutContext;
  intent: CustomerSubscriptionCheckoutIntent;
  billingDetails: BillingDetails;
  onConfirm: () => Promise<void>;
}

const isEnabledCheckoutField = (field: PublicCheckoutFieldSetting) => field.enabled;

const resolveCheckoutLabelKey = (key: PublicCheckoutFieldKey) => {
  switch (key) {
    case 'company':
      return 'checkout.fields.company';
    case 'contact_phone':
      return 'checkout.fields.contact_phone';
    case 'billing.address_line_1':
      return 'checkout.fields.billing.address_line_1';
    case 'billing.address_line_2':
      return 'checkout.fields.billing.address_line_2';
    case 'billing.address_city':
      return 'checkout.fields.billing.address_city';
    case 'billing.address_state':
      return 'checkout.fields.billing.address_state';
    case 'billing.address_postal_code':
      return 'checkout.fields.billing.address_postal_code';
    case 'billing.address_country':
      return 'checkout.fields.billing.address_country';
    default:
      return null;
  }
};

const resolveCheckoutPlaceholderKey = (key: PublicCheckoutFieldKey) => {
  switch (key) {
    case 'company':
      return 'checkout.placeholders.company';
    case 'contact_phone':
      return 'checkout.placeholders.contact_phone';
    case 'billing.address_line_1':
      return 'checkout.placeholders.billing.address_line_1';
    case 'billing.address_line_2':
      return 'checkout.placeholders.billing.address_line_2';
    case 'billing.address_city':
      return 'checkout.placeholders.billing.address_city';
    case 'billing.address_state':
      return 'checkout.placeholders.billing.address_state';
    case 'billing.address_postal_code':
      return 'checkout.placeholders.billing.address_postal_code';
    case 'billing.address_country':
      return 'checkout.placeholders.billing.address_country';
    default:
      return null;
  }
};

function StripePaymentForm({ context, intent, billingDetails, onConfirm }: StripePaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);

    // Validate the rest of the form (PaymentElement) before confirming the intent.
    const submitResult = await elements.submit();
    if (submitResult.error) {
      setIsSubmitting(false);
      toast.error(submitResult.error.message ?? t('customer.planManagement.checkout.errors.confirm'));
      return;
    }

    const intentType = intent.stripeIntentType ?? context.stripeIntentType ?? 'payment';
    const returnUrl = window.location.href;
    const result =
      intentType === 'setup'
        ? await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
            confirmParams: {
              payment_method_data: {
                billing_details: billingDetails,
              },
              return_url: returnUrl,
            } satisfies ConfirmSetupData,
          })
        : await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
              payment_method_data: {
                billing_details: billingDetails,
              },
              return_url: returnUrl,
            } satisfies ConfirmPaymentData,
          });

    if (result.error) {
      setIsSubmitting(false);
      toast.error(result.error.message ?? t('customer.planManagement.checkout.errors.confirm'));
      return;
    }

    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium">{t('customer.planManagement.checkout.cardDetails')}</p>
          <p className="text-xs text-muted-foreground">{t('customer.planManagement.checkout.cardDetailsHelp')}</p>
        </div>
        <div className="rounded-lg border bg-muted/10 p-4">
          <PaymentElement />
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4" />
          <span>{t('customer.planManagement.checkout.securityNote')}</span>
        </p>
        <Button type="submit" disabled={!stripe || isSubmitting} className="w-full sm:w-auto">
          {isSubmitting
            ? t('customer.planManagement.checkout.processing')
            : t('customer.planManagement.checkout.confirmPay')}
        </Button>
      </div>
    </form>
  );
}

type BillingFieldKey =
  | 'billing.address_line_1'
  | 'billing.address_line_2'
  | 'billing.address_city'
  | 'billing.address_state'
  | 'billing.address_postal_code'
  | 'billing.address_country';

type CheckoutFieldErrors = Partial<Record<PublicCheckoutFieldKey, string>>;

type BillingFormState = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

const normalizeOptional = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeRequiredString = (value: string): string => value.trim();

const resolveBillingStateValue = (state: BillingFormState, key: BillingFieldKey): string => {
  switch (key) {
    case 'billing.address_line_1':
      return state.line1;
    case 'billing.address_line_2':
      return state.line2;
    case 'billing.address_city':
      return state.city;
    case 'billing.address_state':
      return state.state;
    case 'billing.address_postal_code':
      return state.postalCode;
    case 'billing.address_country':
      return state.country;
    default:
      return '';
  }
};

const setBillingStateValue = (
  prev: BillingFormState,
  key: BillingFieldKey,
  value: string,
): BillingFormState => {
  switch (key) {
    case 'billing.address_line_1':
      return { ...prev, line1: value };
    case 'billing.address_line_2':
      return { ...prev, line2: value };
    case 'billing.address_city':
      return { ...prev, city: value };
    case 'billing.address_state':
      return { ...prev, state: value };
    case 'billing.address_postal_code':
      return { ...prev, postalCode: value };
    case 'billing.address_country':
      return { ...prev, country: value };
    default:
      return prev;
  }
};

interface SubscriptionCheckoutFormProps {
  context: CustomerSubscriptionCheckoutContext;
  intent: CustomerSubscriptionCheckoutIntent | null;
  onCreateIntent: () => Promise<void>;
  onConfirm: (
    purchaseIntentId: string,
    billingAddress?: Omit<CustomerSubscriptionCheckoutConfirmRequest, 'purchase_intent_id'>,
  ) => Promise<void>;
  forceCheckout?: boolean;
  checkoutFields?: PublicCheckoutFieldSetting[];
  countryOptions?: PublicCheckoutCountryOptions | null;
  isCreatingIntent?: boolean;
}

export function SubscriptionCheckoutForm({
  context,
  intent,
  onCreateIntent,
  onConfirm,
  forceCheckout = false,
  checkoutFields = [],
  countryOptions = null,
  isCreatingIntent = false,
}: SubscriptionCheckoutFormProps) {
  const { t, i18n } = useTranslation();
  const theme = useResolvedTheme();
  const stripeAppearance = useMemo(() => buildStripeAppearance(theme), [theme]);
  const stripeLocale = useMemo(() => resolveStripeLocale(i18n.language), [i18n.language]);
  const [isConfirmingWithoutPayment, setIsConfirmingWithoutPayment] = useState(false);
  const lastLoggedMissingClientSecretIntentId = useRef<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<CheckoutFieldErrors>({});
  const fieldInputRefs = useRef<Partial<Record<PublicCheckoutFieldKey, HTMLElement | null>>>({});
  const [companyValue, setCompanyValue] = useState('');
  const [contactPhoneValue, setContactPhoneValue] = useState('');

  const enabledCheckoutFields = useMemo(() => {
    return checkoutFields
      .filter(isEnabledCheckoutField)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [checkoutFields]);

  const enabledContactKeys = useMemo(() => {
    return enabledCheckoutFields
      .map((field) => field.key)
      .filter((key): key is 'company' | 'contact_phone' => !key.startsWith('billing.'));
  }, [enabledCheckoutFields]);

  const enabledBillingKeys = useMemo(() => {
    return enabledCheckoutFields
      .map((field) => field.key)
      .filter((key): key is BillingFieldKey => key.startsWith('billing.')) as BillingFieldKey[];
  }, [enabledCheckoutFields]);

  const requiredKeys = useMemo(() => {
    return enabledCheckoutFields.filter((field) => field.required).map((field) => field.key);
  }, [enabledCheckoutFields]);

  const [billingState, setBillingState] = useState<BillingFormState>(() => ({
    line1: context.billingAddress.line1 ?? '',
    line2: context.billingAddress.line2 ?? '',
    city: context.billingAddress.city ?? '',
    state: context.billingAddress.state ?? '',
    postalCode: context.billingAddress.postalCode ?? '',
    country: context.billingAddress.country ?? '',
  }));

  useEffect(() => {
    setBillingState({
      line1: context.billingAddress.line1 ?? '',
      line2: context.billingAddress.line2 ?? '',
      city: context.billingAddress.city ?? '',
      state: context.billingAddress.state ?? '',
      postalCode: context.billingAddress.postalCode ?? '',
      country: context.billingAddress.country ?? '',
    });
  }, [
    context.billingAddress.city,
    context.billingAddress.country,
    context.billingAddress.line1,
    context.billingAddress.line2,
    context.billingAddress.postalCode,
    context.billingAddress.state,
  ]);

  const stripeConfig: CustomerCheckoutStripeConfig | null = (() => {
    // Prefer intent-time Stripe config; fall back to context; env var is a last resort.
    const candidate = intent?.stripe ?? context.stripe ?? null;
    if (candidate?.publishableKey) return candidate;

    const envKey = typeof import.meta.env.VITE_STRIPE_PUBLIC_KEY === 'string' ? import.meta.env.VITE_STRIPE_PUBLIC_KEY : '';
    const normalizedEnvKey = envKey.trim();
    if (!normalizedEnvKey) return candidate;

    return {
      publishableKey: normalizedEnvKey,
      accountId: null,
      accountType: null,
    };
  })();

  const publishableKey = stripeConfig?.publishableKey?.trim() ?? '';
  const stripeAccountOption = resolveStripeAccountOption(stripeConfig);

  const stripePromise = useMemo(() => {
    if (!isStripeReady(stripeConfig)) return null;
    return loadStripe(publishableKey, stripeAccountOption);
  }, [publishableKey, stripeAccountOption?.stripeAccount, stripeConfig?.accountType]);

  // NOTE: Keep hooks above all conditional returns; production builds will throw otherwise.
  const purchaseIntentId = intent?.purchaseIntentId ?? '';
  const paymentRequired = intent?.paymentRequired ?? false;
  const intentStripeIntentType = intent?.stripeIntentType ?? null;
  const stripeSubscriptionId = intent?.stripeSubscriptionId ?? null;
  const stripePaymentIntentId = intent?.stripePaymentIntentId ?? null;
  const stripeSetupIntentId = intent?.stripeSetupIntentId ?? null;
  const clientSecret = intent?.clientSecret?.trim() ?? '';

  const resolveBillingPayload = (): Omit<CustomerSubscriptionCheckoutConfirmRequest, 'purchase_intent_id'> => ({
    company: normalizeOptional(companyValue),
    contact_phone: normalizeOptional(contactPhoneValue),
    billing_address_line_1: normalizeOptional(billingState.line1),
    billing_address_line_2: normalizeOptional(billingState.line2),
    billing_address_city: normalizeOptional(billingState.city),
    billing_address_state: normalizeOptional(billingState.state),
    billing_address_postal_code: normalizeOptional(billingState.postalCode),
    billing_address_country: normalizeOptional(billingState.country),
  });

  const resolveStripeBillingDetails = (): BillingDetails => {
    // `BillingDetails.address` must be an object (Stripe types do not accept undefined for some element flows).
    // Provide a partial address with optional values.
    return {
      name: '',
      phone: normalizeOptional(contactPhoneValue),
      address: {
        line1: normalizeRequiredString(billingState.line1),
        line2: normalizeOptional(billingState.line2) ?? null,
        city: normalizeRequiredString(billingState.city),
        state: normalizeRequiredString(billingState.state),
        postal_code: normalizeRequiredString(billingState.postalCode),
        country: normalizeRequiredString(billingState.country),
      },
    };
  };

  const resolveFieldValue = (key: PublicCheckoutFieldKey): string => {
    switch (key) {
      case 'company':
        return companyValue;
      case 'contact_phone':
        return contactPhoneValue;
      case 'billing.address_line_1':
      case 'billing.address_line_2':
      case 'billing.address_city':
      case 'billing.address_state':
      case 'billing.address_postal_code':
      case 'billing.address_country':
        return resolveBillingStateValue(billingState, key);
      default:
        return '';
    }
  };

  const clearFieldError = (key: PublicCheckoutFieldKey) => {
    if (!fieldErrors[key]) return;
    setFieldErrors((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const validateFields = (): boolean => {
    if (!enabledCheckoutFields.length) {
      setFieldErrors({});
      return true;
    }

    const next: CheckoutFieldErrors = {};
    const requiredMessage = t(
      'customer.planManagement.checkout.errors.requiredField',
      'This field is required.',
    );

    requiredKeys.forEach((key) => {
      const value = resolveFieldValue(key);
      if (!value.trim()) {
        next[key] = requiredMessage;
      }
    });

    setFieldErrors(next);

    const firstInvalidKey = (Object.keys(next)[0] as PublicCheckoutFieldKey | undefined) ?? null;
    if (firstInvalidKey) {
      const element = fieldInputRefs.current[firstInvalidKey];
      element?.focus();
    }

    return Object.keys(next).length === 0;
  };

  const handleInvalidFields = () => {
    const ok = validateFields();
    if (!ok) {
      toast.error(t('customer.planManagement.checkout.errors.requiredFieldsMissing'));
    }
  };

  const hasCheckoutFields = enabledCheckoutFields.length > 0;

  useEffect(() => {
    if (!purchaseIntentId) return;
    if (!paymentRequired) return;
    if (clientSecret && clientSecret.includes('_secret_')) return;
    if (lastLoggedMissingClientSecretIntentId.current === purchaseIntentId) return;
    lastLoggedMissingClientSecretIntentId.current = purchaseIntentId;

    logger.error('[subscription-checkout] Missing/invalid client_secret for required checkout intent', {
      purchaseIntentId,
      stripeIntentType: intentStripeIntentType ?? context.stripeIntentType ?? null,
      stripeSubscriptionId,
      stripePaymentIntentId,
      stripeSetupIntentId,
      paymentRequired,
      hasClientSecret: Boolean(clientSecret),
    });
  }, [
    clientSecret,
    context.stripeIntentType,
    intentStripeIntentType,
    paymentRequired,
    purchaseIntentId,
    stripePaymentIntentId,
    stripeSetupIntentId,
    stripeSubscriptionId,
  ]);

  // If checkout is only about collecting a payment method (setup intent), we still need to proceed.
  if (!context.requiresPayment && context.stripeIntentType === null && !forceCheckout && !hasCheckoutFields) {
    return (
      <div className="rounded-lg border bg-emerald-50 p-4 text-sm text-emerald-900">
        {t('customer.planManagement.checkout.noPaymentRequired')}
      </div>
    );
  }

  const CheckoutFields = hasCheckoutFields ? (
    <div className="space-y-5">
      {enabledContactKeys.length ? (
        <div className="space-y-3">
          <p className="text-sm font-medium">{t('checkout.contactDetailsTitle')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {enabledContactKeys.map((key) => {
              const labelKey = resolveCheckoutLabelKey(key);
              const placeholderKey = resolveCheckoutPlaceholderKey(key);
              const id = `checkout-${key.replace(/[^a-z0-9]+/gi, '-')}`;
              const errorMessage = fieldErrors[key];
              const required = requiredKeys.includes(key);
              const value = resolveFieldValue(key);
              return (
                <div key={key} className="space-y-2">
                  <Label htmlFor={id}>
                    {labelKey ? t(labelKey) : key}
                    {required ? <span className="text-destructive"> *</span> : null}
                  </Label>
                  <Input
                    id={id}
                    ref={(node) => {
                      fieldInputRefs.current[key] = node;
                    }}
                    value={value}
                    placeholder={placeholderKey ? t(placeholderKey) : undefined}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (key === 'company') {
                        setCompanyValue(nextValue);
                      } else if (key === 'contact_phone') {
                        setContactPhoneValue(nextValue);
                      }
                      clearFieldError(key);
                    }}
                    onBlur={() => {
                      if (!required) return;
                      if (resolveFieldValue(key).trim()) {
                        clearFieldError(key);
                        return;
                      }
                      setFieldErrors((prev) => ({
                        ...prev,
                        [key]: t('customer.planManagement.checkout.errors.requiredField', 'This field is required.'),
                      }));
                    }}
                    aria-invalid={Boolean(errorMessage)}
                    className={errorMessage ? 'border-destructive focus-visible:ring-destructive/30' : undefined}
                  />
                  {errorMessage ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {enabledBillingKeys.length ? (
        <div className="space-y-3">
          <p className="text-sm font-medium">{t('checkout.billingAddressTitle')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {enabledBillingKeys.map((key) => {
              const labelKey = resolveCheckoutLabelKey(key);
              const placeholderKey = resolveCheckoutPlaceholderKey(key);
              const id = `checkout-${key.replace(/[^a-z0-9]+/gi, '-')}`;
              const errorMessage = fieldErrors[key];
              const required = requiredKeys.includes(key);
              return (
                <div key={key} className="space-y-2">
                  <Label htmlFor={id}>
                    {labelKey ? t(labelKey) : key}
                    {required ? <span className="text-destructive"> *</span> : null}
                  </Label>
                  {key === 'billing.address_country' ? (
                    <CountrySelect
                      id={id}
                      value={resolveBillingStateValue(billingState, key)}
                      placeholder={placeholderKey ? t(placeholderKey) : undefined}
                      priorityOptions={countryOptions?.priority ?? []}
                      remainingOptions={countryOptions?.remaining ?? []}
                      onChange={(nextValue) => {
                        setBillingState((prev) => setBillingStateValue(prev, key, nextValue));
                        clearFieldError(key);
                      }}
                      ariaInvalid={Boolean(errorMessage)}
                    />
                  ) : (
                    <Input
                      id={id}
                      ref={(node) => {
                        fieldInputRefs.current[key] = node;
                      }}
                      value={resolveBillingStateValue(billingState, key)}
                      placeholder={placeholderKey ? t(placeholderKey) : undefined}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setBillingState((prev) => setBillingStateValue(prev, key, nextValue));
                        clearFieldError(key);
                      }}
                      onBlur={() => {
                        if (!required) return;
                        const value = resolveBillingStateValue(billingState, key);
                        if (value.trim()) {
                          clearFieldError(key);
                          return;
                        }
                        setFieldErrors((prev) => ({
                          ...prev,
                          [key]: t('customer.planManagement.checkout.errors.requiredField', 'This field is required.'),
                        }));
                      }}
                      aria-invalid={Boolean(errorMessage)}
                      className={errorMessage ? 'border-destructive focus-visible:ring-destructive/30' : undefined}
                    />
                  )}
                  {errorMessage ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  ) : null;

  if (!intent) {
    return (
      <div className="space-y-5">
        {CheckoutFields}

        <div className="flex items-start gap-3 rounded-lg border bg-muted/10 p-4">
          <CreditCard className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('customer.planManagement.checkout.paymentIntroTitle')}</p>
            <p className="text-sm text-muted-foreground">{t('customer.planManagement.checkout.paymentIntro')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4" />
            <span>{t('customer.planManagement.checkout.securityNote')}</span>
          </p>
          <Button
            type="button"
            onClick={async () => {
              if (!validateFields()) return handleInvalidFields();
              await onCreateIntent();
            }}
            disabled={isCreatingIntent}
            className="w-full sm:w-auto"
          >
            {isCreatingIntent
              ? t('customer.planManagement.checkout.initializingPayment')
              : t('customer.planManagement.checkout.initializePayment')}
          </Button>
        </div>
      </div>
    );
  }

  // Some backends can create a checkout intent that does not require (re)collecting payment details.
  // In this case, skip Stripe Elements entirely and just confirm the intent.
  if (!intent.paymentRequired && !forceCheckout) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertTitle>{t('customer.planManagement.checkout.noPaymentTitle')}</AlertTitle>
          <AlertDescription>{t('customer.planManagement.checkout.noPaymentRequired')}</AlertDescription>
        </Alert>
        {CheckoutFields}
        <Button
          type="button"
          onClick={async () => {
            if (!intent.purchaseIntentId) return;
            if (!validateFields()) return handleInvalidFields();
            try {
              setIsConfirmingWithoutPayment(true);
              await onConfirm(intent.purchaseIntentId, resolveBillingPayload());
            } finally {
              setIsConfirmingWithoutPayment(false);
            }
          }}
          disabled={!intent.purchaseIntentId || isConfirmingWithoutPayment}
          className="w-full sm:w-auto"
        >
          {isConfirmingWithoutPayment
            ? t('customer.planManagement.actions.processing')
            : t('customer.planManagement.actions.activateSubscription')}
        </Button>
      </div>
    );
  }

  if (!clientSecret || !clientSecret.includes('_secret_')) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>{t('customer.planManagement.checkout.errors.clientSecretTitle')}</AlertTitle>
          <AlertDescription>{t('customer.planManagement.checkout.errors.clientSecret')}</AlertDescription>
        </Alert>
        <Button type="button" onClick={onCreateIntent} disabled={isCreatingIntent} className="w-full sm:w-auto">
          {isCreatingIntent
            ? t('customer.planManagement.checkout.initializingPayment')
            : t('customer.planManagement.checkout.initializePayment')}
        </Button>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="rounded-lg border bg-amber-50 p-4 text-sm text-amber-900">
        {t('customer.planManagement.checkout.missingStripe')}
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: stripeAppearance,
        locale: stripeLocale,
      }}
    >
      <div className="space-y-4">
        {CheckoutFields}
        <StripePaymentForm
          context={context}
          intent={intent}
          billingDetails={resolveStripeBillingDetails()}
          onConfirm={async () => {
            if (!validateFields()) return handleInvalidFields();
            await onConfirm(intent.purchaseIntentId, resolveBillingPayload());
          }}
        />
      </div>
    </Elements>
  );
}
